/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { BsPrinter, BsArrowRepeat, BsDropletHalf, BsBoxSeam, BsGeoAlt, BsClockHistory } from 'react-icons/bs';
// import { tubeServices } from '../../../../service/fileServices/tubeInventory';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useTranslation } from 'react-i18next';
import { helperFunctions } from '../../../../utils/helper';
import { BiSolidFlask } from 'react-icons/bi';

interface TubeDetailsViewModalProps {
  showView: boolean;
  setShowView: (value: boolean) => void;
  tubeId: string;
}

const TubeDetailsViewModal: React.FC<TubeDetailsViewModalProps> = ({
  showView,
  setShowView,
  tubeId,
}) => {

  const { t } = useTranslation();

  const areaId = useSelector((state: RootState) => state.area.areaId) || '';

  const [singleTubeDetails, setSingleTubeDetails] = useState<any>({});
  const [tubeInfoDetails, setTubeInfoDetails] = useState<any>({});
  const [mediaFillDetails, setMediaFillDetails] = useState<any>({});
  const [inventoryDetails, setInventoryDetails] = useState<any>({});
  const [locationDetails, setLocationDetails] = useState<any>({});
  const [lifecycleDetails, setLifecycleDetails] = useState<any>({});
  const [autoclaveDetails, setAutoclaveDetails] = useState<any>({});
  const [historyDetails, setHistoryDetails] = useState<any>([]);

  const [tabValue, setTabValue] = useState("tubeDetails");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const STATUS_BADGE_STYLE: React.CSSProperties = {
    display: 'inline-block',
    padding: '2px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    backgroundColor: '#E6F6EC',
    color: '#1E8E3E',
    };


  // Remove this once the real API endpoint is wired up.
    const MOCK_TUBE_RESPONSE = {
        status: true,
        statusCode: 200,
        data: {
            tube_id: "TUBE-2025-007856",
            tube_code: "TM-001245",
            media_name: "Tryptic Soy Broth (TSB)",
            media_type: "Liquid Media",
            fill_volume: "10 mL",
            tube_type: "Screw Cap Test Tube",
            current_status: "AVAILABLE",

            manufactured_by: "Zent EM Labs",
            tube_size: "16 mm x 100 mm",
            tube_category: "In-house",
            manufacturing_date: "2025-07-23",
            expiry_date: "2026-07-22",
            shelf_life: "12 Months",
            external_lot_no: "EXT-78645",
            batch_quantity: "500",
            unit_of_measure: "Nos",

            media_lot_no: "ML-TSB-250723",
            media_prepared_on: "2025-07-23",
            media_prepared_by: "Vincy U",
            ph_at_fill: "7.2",

            available_from: "2025-07-24 09:00",
            quantity_status: "In Stock",
            usable: "Yes",
            hold_status: "No",

            current_location: "Store Room - Shelf B3",
            storage_condition: "15 - 30 \u00b0C",
            storage_area: "Media Store",
            rack_box: "RB-07",
            position: "P3",

            times_used: 0,
            last_used_on: "",
            linked_test_batch: "",
            discarded: "No",
            discard_date: "",

            autoclave_cycle_no: "AC-2025-0341",
            autoclave_start_time: "2025-07-22 08:00",
            autoclave_end_time: "2025-07-22 09:15",
            autoclaved_by: "Ravi Kumar",

            history_details: [
                {
                    action: "Created",
                    performed_by: "Vincy U",
                    date_time: "2025-07-23 10:05",
                    remarks: "Media tube prepared and logged",
                },
                {
                    action: "Moved to Store Room",
                    performed_by: "Ravi Kumar",
                    date_time: "2025-07-24 09:00",
                    remarks: "Placed in Shelf B3, Rack RB-07",
                },
            ],
        },
    };

  // section refs, used so clicking a sidebar item scrolls to the matching section
//   const sectionRefs = {
//     tubeDetails: React.useRef<HTMLDivElement>(null),
//     mediaFill: React.useRef<HTMLDivElement>(null),
//     inventory: React.useRef<HTMLDivElement>(null),
//     location: React.useRef<HTMLDivElement>(null),
//     lifecycle: React.useRef<HTMLDivElement>(null),
//     autoclave: React.useRef<HTMLDivElement>(null),
//     history: React.useRef<HTMLDivElement>(null),
//   };

  // function to get tube details by id
  const scanTubeById = async (id: any) => {

    const dataToSend = {
      "id": id,
      "lab_area_id": areaId
    }

    //const response = await tubeServices.scanTubeById(dataToSend);
    const response = await Promise.resolve(MOCK_TUBE_RESPONSE);

    if (response?.status && response?.statusCode === 200) {
      const data = response.data;

      const basicDetails = {
        tubeId: data.tube_id || "-",
        tubeCode: data.tube_code || "-",
        mediaName: data.media_name || "-",
        mediaType: data.media_type || "-",
        fillVolume: data.fill_volume || "-",
        tubeType: data.tube_type || "-",
        currentStatus: data.current_status || "-",
      }

      const tubeInfo = {
        manufacturedBy: data.manufactured_by || "-",
        tubeSize: data.tube_size || "-",
        tubeCategory: data.tube_category || "-",
        manufacturingDate: helperFunctions.formatDate(data.manufacturing_date || "-", true),
        expiryDate: helperFunctions.formatDate(data.expiry_date || "-", true),
        shelfLife: data.shelf_life || "-",
        externalLotNo: data.external_lot_no || "-",
        batchQuantity: data.batch_quantity || "-",
        unitOfMeasure: data.unit_of_measure || "-",
      }

      const mediaFill = {
        mediaLotNo: data.media_lot_no || "-",
        mediaPreparedOn: helperFunctions.formatDate(data.media_prepared_on || "-", true),
        mediaPreparedBy: data.media_prepared_by || "-",
        fillVolume: data.fill_volume || "-",
        phAtFill: data.ph_at_fill || "-",
      }

      const inventory = {
        currentStatus: data.current_status || "-",
        availableFrom: helperFunctions.formatDate(data.available_from || "-", true),
        quantityStatus: data.quantity_status || "-",
        usable: data.usable || "-",
        holdStatus: data.hold_status || "-",
      }

      const location = {
        currentLocation: data.current_location || "-",
        storageCondition: data.storage_condition || "-",
        storageArea: data.storage_area || "-",
        rackBox: data.rack_box || "-",
        position: data.position || "-",
      }

      const lifecycle = {
        timesUsed: data.times_used ?? "-",
        lastUsedOn: helperFunctions.formatDate(data.last_used_on || "-", true),
        linkedTestBatch: data.linked_test_batch || "-",
        discarded: data.discarded || "No",
        discardDate: helperFunctions.formatDate(data.discard_date || "-", true),
      }

      const autoclave = {
        autoclaveCycleNo: data.autoclave_cycle_no || "-",
        autoclaveStartTime: helperFunctions.formatDate(data.autoclave_start_time || "-", true),
        autoclaveEndTime: helperFunctions.formatDate(data.autoclave_end_time || "-", true),
        autoclavedBy: data.autoclaved_by || "-",
      }

      const history: any = data.history_details?.map((element: any) => {
        return {
          action: element.action || "-",
          performedBy: element.performed_by || "-",
          dateAndTime: helperFunctions.formatDate(element.date_time || "-", true),
          remarks: element.remarks || "-",
        }
      }) || [];

      setSingleTubeDetails(basicDetails);
      setTubeInfoDetails(tubeInfo);
      setMediaFillDetails(mediaFill);
      setInventoryDetails(inventory);
      setLocationDetails(location);
      setLifecycleDetails(lifecycle);
      setAutoclaveDetails(autoclave);
      setHistoryDetails(history);
    }
  }

  // refresh the tube details
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await scanTubeById(tubeId);
    setIsRefreshing(false);
  };

  // print the tube details
  const handlePrint = () => {
    window.print();
  };

  // close tube details modal
  const handleClose = () => {
    setShowView(false)
    setSingleTubeDetails({})
    setTubeInfoDetails({})
    setMediaFillDetails({})
    setInventoryDetails({})
    setLocationDetails({})
    setLifecycleDetails({})
    setAutoclaveDetails({})
    setHistoryDetails([])
    setTabValue("tubeDetails")
  };

  // add css to selected sidebar item and scroll to the matching section
  const getItemClass = (value: string) => {
    return `custom-list-group-item ${tabValue === value ? 'selected' : ''}`;
  };

  const handleTabClick = (value: string) => {
    setTabValue(value);
    // sectionRefs[value]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Render initial api call
  useEffect(() => {
    if (showView) {
      scanTubeById(tubeId);
    }
  }, [showView, tubeId]);

  return (
    <Modal className="view-modal" show={showView} size="lg">
      <Modal.Header closeButton onClick={handleClose}>
        <Modal.Title className="view-header">{t("tubeDetailsView.view")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="view-body">
        <div className="view-body-content">
          <p className="plate-details">{t("tubeDetailsView.tubeDetails")}</p>
          <div className="view-body-details">
            <div className="row">
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.tubeId")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.tubeId}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.tubeCode")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.tubeCode}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.mediaName")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.mediaName}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.mediaType")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.mediaType}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.fillVolume")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.fillVolume}</p>
              </div>
            </div>
            <div className="row">
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.tubeType")}</p>
                <p className="plate-detail-value">{singleTubeDetails?.tubeType}</p>
              </div>
              <div className="col-md-2">
                <p className="plate-detail-key">{t("tubeDetailsView.currentStatus")}</p>
                <p className='plate-detail-value' style={STATUS_BADGE_STYLE}>{singleTubeDetails?.currentStatus}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="view-lab-details">
          <div className="view-lab-details-content">
            <div className="row">
              <ul className="custom-list-group col-md-3">
                <li className={getItemClass("tubeDetails")} onClick={() => handleTabClick("tubeDetails")}>{t("tubeDetailsView.tubeDetails")}</li>
                <li className={getItemClass("mediaFill")} onClick={() => handleTabClick("mediaFill")}>{t("tubeDetailsView.mediaFillDetails")}</li>
                <li className={getItemClass("inventory")} onClick={() => handleTabClick("inventory")}>{t("tubeDetailsView.inventoryDetails")}</li>
                <li className={getItemClass("location")} onClick={() => handleTabClick("location")}>{t("tubeDetailsView.locationDetails")}</li>
                <li className={getItemClass("lifecycle")} onClick={() => handleTabClick("lifecycle")}>{t("tubeDetailsView.lifecycleUsage")}</li>
                <li className={getItemClass("autoclave")} onClick={() => handleTabClick("autoclave")}>{t("tubeDetailsView.autoclaveDetails")}</li>
                <li className={getItemClass("history")} onClick={() => handleTabClick("history")}>{t("tubeDetailsView.history")}</li>
              </ul>

              <div className="col-md-9 tube-detail-sections">

                {/* Tube Information */}
                {tabValue === "tubeDetails" && (
                <div className="detail-section">
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="lab-detail-key">{t("tubeDetailsView.tubeInformation")}</p>
                  </div>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.manufacturedBy")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.manufacturedBy || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.tubeSize")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.tubeSize || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.tubeCategory")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.tubeCategory || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.manufacturingDate")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.manufacturingDate || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.expiryDate")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.expiryDate || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.shelfLife")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.shelfLife || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.externalLotNo")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.externalLotNo || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.batchQuantity")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.batchQuantity || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.unitOfMeasure")}</p>
                      <p className="plate-detail-value">{tubeInfoDetails?.unitOfMeasure || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* Media & Fill Details */}
                {tabValue === "mediaFill" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.mediaFillDetails")}</p>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.mediaLotNo")}</p>
                      <p className="plate-detail-value">{mediaFillDetails?.mediaLotNo || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.mediaPreparedOn")}</p>
                      <p className="plate-detail-value">{mediaFillDetails?.mediaPreparedOn || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.mediaPreparedBy")}</p>
                      <p className="plate-detail-value">{mediaFillDetails?.mediaPreparedBy || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.fillVolume")}</p>
                      <p className="plate-detail-value">{mediaFillDetails?.fillVolume || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.phAtFill")}</p>
                      <p className="plate-detail-value">{mediaFillDetails?.phAtFill || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* Inventory Details */}
                {tabValue === "inventory" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.inventoryDetails")}</p>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.currentStatus")}</p>
                      <p className='plate-detail-value' style={STATUS_BADGE_STYLE}>{inventoryDetails?.currentStatus || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.availableFrom")}</p>
                      <p className="plate-detail-value">{inventoryDetails?.availableFrom || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.quantityStatus")}</p>
                      <p className="plate-detail-value">{inventoryDetails?.quantityStatus || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.usable")}</p>
                      <p className="plate-detail-value">{inventoryDetails?.usable || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.holdStatus")}</p>
                      <p className="plate-detail-value">{inventoryDetails?.holdStatus || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* Location Details */}
                {tabValue === "location" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.locationDetails")}</p>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.currentLocation")}</p>
                      <p className="plate-detail-value">{locationDetails?.currentLocation || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.storageCondition")}</p>
                      <p className="plate-detail-value">{locationDetails?.storageCondition || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.storageArea")}</p>
                      <p className="plate-detail-value">{locationDetails?.storageArea || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.rackBox")}</p>
                      <p className="plate-detail-value">{locationDetails?.rackBox || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.position")}</p>
                      <p className="plate-detail-value">{locationDetails?.position || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* Lifecycle & Usage */}
                {tabValue === "lifecycle" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.lifecycleUsage")}</p>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.timesUsed")}</p>
                      <p className="plate-detail-value">{lifecycleDetails?.timesUsed ?? "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.lastUsedOn")}</p>
                      <p className="plate-detail-value">{lifecycleDetails?.lastUsedOn || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.linkedTestBatch")}</p>
                      <p className="plate-detail-value">{lifecycleDetails?.linkedTestBatch || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.discarded")}</p>
                      <p className="plate-detail-value">{lifecycleDetails?.discarded || "No"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.discardDate")}</p>
                      <p className="plate-detail-value">{lifecycleDetails?.discardDate || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* Autoclave Details */}
                {tabValue === "autoclave" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.autoclaveDetails")}</p>
                  <div className="detail-col-1 row">
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.autoclaveCycleNo")}</p>
                      <p className="plate-detail-value">{autoclaveDetails?.autoclaveCycleNo || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.autoclaveStartDate")}</p>
                      <p className="plate-detail-value">{autoclaveDetails?.autoclaveStartTime || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.autoclaveEndDate")}</p>
                      <p className="plate-detail-value">{autoclaveDetails?.autoclaveEndTime || "-"}</p>
                    </div>
                    <div className="col-md-4">
                      <p className="plate-detail-key">{t("tubeDetailsView.autoclavedBy")}</p>
                      <p className="plate-detail-value">{autoclaveDetails?.autoclavedBy || "-"}</p>
                    </div>
                  </div>
                </div>
                )}

                {/* History */}
                {tabValue === "history" && (
                <div className="detail-section">
                  <p className="lab-detail-key">{t("tubeDetailsView.history")}</p>
                  {historyDetails?.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr style={{ fontSize: "14px" }}>
                          <th scope="col">{t("tubeDetailsView.action")}</th>
                          <th scope="col">{t("tubeDetailsView.performedBy")}</th>
                          <th scope="col">{t("tubeDetailsView.dateAndTime")}</th>
                          <th scope="col">{t("tubeDetailsView.remarks")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyDetails.map((element: any, index: number) => (
                          <tr key={index} style={{ fontSize: "11px" }}>
                            <td>{element.action}</td>
                            <td>{element.performedBy}</td>
                            <td>{element.dateAndTime}</td>
                            <td>{element.remarks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="plate-detail-value">{t("tubeDetailsView.noHistory")}</p>
                  )}
                </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="custom-modal-footer">
        <Button className="footerCancelBtn" variant="tertiory" onClick={handleClose}>
          {t("buttons.cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TubeDetailsViewModal;